// Role-based access control — frontend mirror of backend's permission map.
// Keep in sync with /app/backend/server.py (require_* dependencies near top of file).

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",       // legacy alias of super_admin
  STAFF: "staff",
  ACCOUNTS: "accounts",
};

// Roles that have full access (sidebar & every admin page).
const FULL = [ROLES.SUPER_ADMIN, ROLES.ADMIN];

/**
 * Allow-list per admin section. `[...FULL, "staff"]` means super_admin + admin + staff can access.
 * The keys here match the `path` segment in `/admin/<path>`.
 */
export const SECTION_ROLES = {
  dashboard:             [...FULL, ROLES.STAFF, ROLES.ACCOUNTS],
  leads:                 [...FULL, ROLES.STAFF],
  plans:                 [...FULL, ROLES.ACCOUNTS],
  members:               [...FULL, ROLES.STAFF],
  payments:              [...FULL, ROLES.ACCOUNTS],
  renewals:              [...FULL, ROLES.STAFF],
  corporate:             [...FULL, ROLES.STAFF],
  employees:             [...FULL],
  referrals:             [...FULL, ROLES.STAFF],
  "employee-performance":[...FULL, ROLES.ACCOUNTS],
  payouts:               [...FULL, ROLES.ACCOUNTS],
  verify:                [...FULL, ROLES.STAFF],
  invoices:              [...FULL, ROLES.ACCOUNTS, ROLES.STAFF],
  "admin-logs":          [ROLES.SUPER_ADMIN, ROLES.ADMIN],
  settings:              [ROLES.SUPER_ADMIN, ROLES.ADMIN],
  users:                 [ROLES.SUPER_ADMIN],
  roles:                 [ROLES.SUPER_ADMIN],
  visits:                [...FULL, ROLES.STAFF, ROLES.ACCOUNTS],
  "plan-benefits":       [...FULL],
};

export function canAccess(section, roleOrUser) {
  // Accept either a role string (legacy callers) or the full user object so
  // we can honour the dynamic `permissions` array returned by the backend
  // (which reflects what the super-admin saved in Roles & Permissions).
  let role, perms;
  if (roleOrUser && typeof roleOrUser === "object") {
    role = roleOrUser.role;
    perms = roleOrUser.permissions;
  } else {
    role = roleOrUser;
    perms = undefined;
  }
  if (!role) return false;
  // super_admin always allowed (mirrors backend lock).
  if (role === ROLES.SUPER_ADMIN) return true;
  // Dynamic permissions take precedence when the backend provided them.
  if (Array.isArray(perms)) {
    return perms.includes(section);
  }
  // Fallback to the static map (covers stale sessions before /auth/me lands).
  const allowed = SECTION_ROLES[section];
  if (!allowed) return true;
  return allowed.includes(role);
}

import { adminUrl } from "@/lib/adminPath";

/** Landing page for each role — used after login to auto-route. */
export function defaultAdminRoute(role) {
  if (role === ROLES.ACCOUNTS) return adminUrl("/payments");
  if (role === ROLES.STAFF) return adminUrl("/members");
  return adminUrl(); // super_admin / admin
}

/** Mirror of backend ROLE_MEMBERS etc — handy for action-button gating. */
export const CAN = {
  offlineRegister: [...FULL, ROLES.STAFF],
  // Staff can open the edit modal (they edit mobile/address etc + KYC/docs).
  editMember:      [...FULL, ROLES.STAFF],
  // Only super_admin/admin can edit *sensitive* identity/membership fields
  // (name, email, dob, gender, aadhaar, relation). Staff get read-only on these.
  editMemberFull:  [...FULL],
  // UHID is locked once set; only super_admin/admin may set or modify it.
  editUhid:        [...FULL],
  resendEmail:     [...FULL, ROLES.STAFF],
  managePlans:     [...FULL],
  manageEmployees: [...FULL],
  manageAdminUsers:[ROLES.SUPER_ADMIN],
  updateSettings:  [ROLES.SUPER_ADMIN],
};
export const hasCap = (capability, role) => !!role && (CAN[capability] || []).includes(role);
